import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButton,
  IonContent,
  IonFooter,
  IonIcon,
  IonModal,
} from '@ionic/react';
import {
  TOPIC_TYPE_PICKER_SHEET_BREAKPOINT,
  TOPIC_TYPE_PICKER_SHEET_BREAKPOINTS,
  useAppConfig,
} from '@/modules/config';
import type { TopicTypeId } from '@/modules/topic/types';
import { enabledTopicTypes } from '@/modules/topic/topicTypeRegistry';
import { StorageService } from '@/services/storage';
import {
  TOPIC_TYPE_STICKERS,
  startTopicLabel,
  stickerExamples,
} from './stickers';
import styles from './TopicTypePickerSheet.module.css';

export interface TopicTypePickerSheetProps {
  isOpen: boolean;
  /**
   * Called once the sheet has fully dismissed, with the picked type or `null`
   * when it was dismissed without a pick (dragged down, or the list behind it
   * tapped). The caller closes the
   * sheet here (sets `isOpen` false) and, for a pick, navigates to that type's
   * create form — only now, so the modal is torn down before the next page
   * mounts (an abrupt swap leaves the next page inert).
   */
  onDidDismiss: (picked: TopicTypeId | null) => void;
}

/**
 * The New Topic type chooser (component-driven path): a near-full-height sheet
 * over the topics list. Topic type is chosen once here and is then fixed for the
 * life of the topic (the create/edit forms carry no type-selection UI).
 *
 * Each enabled type gets a sticker card in a horizontally snapping carousel, with a
 * row of sticker tabs above it that jumps to a card. The "Start a … Topic" button
 * in the sheet's footer picks the card in view; tapping a card picks it directly.
 * The `topic-type-chip-<id>` testids sit on the cards so the create E2E flow picks a
 * type with one click.
 *
 * Like the app's other sheets, it is dismissed by dragging its handle down or by
 * tapping the dimmed list behind it.
 *
 * Picking asks the modal to dismiss itself; `pickedTypeRef` carries the choice
 * into `onDidDismiss`. The pick is also remembered on the device, and the picker
 * opens on the last picked type (Event until something has been picked). Its own
 * state lives in `PickerBody`, which Ionic mounts only while the sheet is
 * presented, so each opening starts from that type again.
 */
/** The type the picker opens on when nothing has been picked on this device. */
const DEFAULT_PICKER_TOPIC_TYPE: TopicTypeId = 'event';

const TopicTypePickerSheet: React.FC<TopicTypePickerSheetProps> = ({
  isOpen,
  onDidDismiss,
}) => {
  const modalRef = useRef<HTMLIonModalElement>(null);
  const pickedTypeRef = useRef<TopicTypeId | null>(null);
  // The type last picked on this device. Loaded when the sheet first mounts
  // (with the list behind it), so it is in hand by the time the sheet opens.
  const [lastType, setLastType] = useState<string | undefined>();

  useEffect(() => {
    let cancelled = false;
    void StorageService.getLastTopicType().then(type => {
      if (!cancelled) setLastType(type);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const close = (picked: TopicTypeId) => {
    pickedTypeRef.current = picked;
    setLastType(picked);
    void StorageService.setLastTopicType(picked);
    void modalRef.current?.dismiss();
  };

  return (
    <IonModal
      ref={modalRef}
      isOpen={isOpen}
      initialBreakpoint={TOPIC_TYPE_PICKER_SHEET_BREAKPOINT}
      breakpoints={TOPIC_TYPE_PICKER_SHEET_BREAKPOINTS}
      // The call to action stays pinned to the visible bottom of the sheet.
      expandToScroll={false}
      handle
      onWillPresent={() => {
        pickedTypeRef.current = null;
      }}
      onDidDismiss={() => onDidDismiss(pickedTypeRef.current)}
      data-testid="topic-type-picker-sheet"
    >
      <PickerBody lastType={lastType} onPick={id => close(id)} />
    </IonModal>
  );
};

interface PickerBodyProps {
  /** The type last picked on this device; the picker opens on it. */
  lastType: string | undefined;
  onPick: (id: TopicTypeId) => void;
}

const PickerBody: React.FC<PickerBodyProps> = ({ lastType, onPick }) => {
  const { features } = useAppConfig();
  const types = enabledTopicTypes(features);
  const [selected, setSelected] = useState(() => {
    // The last picked type when it is still offered, else the default, else the first.
    for (const id of [lastType, DEFAULT_PICKER_TOPIC_TYPE]) {
      const index = types.findIndex(t => t.id === id);
      if (index >= 0) return index;
    }
    return 0;
  });
  const carouselRef = useRef<HTMLDivElement>(null);
  // While a tab tap is smooth-scrolling the carousel, the cards it passes over
  // must not move the selection; cleared on arrival or when the user grabs it.
  const jumpTargetRef = useRef<number | null>(null);
  const current = types[Math.min(selected, types.length - 1)];

  // Horizontal distance between neighbouring cards' snap points.
  const cardStep = (carousel: HTMLElement): number => {
    const [first, second] = Array.from(carousel.children) as HTMLElement[];
    if (!first) return 0;
    return second ? second.offsetLeft - first.offsetLeft : first.offsetWidth;
  };

  // Open with the initially selected card in view.
  useLayoutEffect(() => {
    const carousel = carouselRef.current;
    if (carousel) carousel.scrollLeft = selected * cardStep(carousel);
    // Only on mount: later selection changes scroll the carousel themselves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const jumpTo = (index: number) => {
    setSelected(index);
    const carousel = carouselRef.current;
    if (!carousel) return;
    jumpTargetRef.current = index;
    carousel.scrollTo?.({
      left: index * cardStep(carousel),
      behavior: 'smooth',
    });
  };

  const onCarouselScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const step = cardStep(e.currentTarget);
    if (!step) return;
    const index = Math.round(e.currentTarget.scrollLeft / step);
    if (jumpTargetRef.current !== null) {
      if (index === jumpTargetRef.current) jumpTargetRef.current = null;
      return;
    }
    if (index !== selected) setSelected(index);
  };

  return (
    <>
      <IonHeader className="ion-no-border">
        <IonToolbar className={styles.toolbar}>
          <IonTitle>Shape your snowball</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className={styles.content} scrollY={false}>
        <div className={styles.body}>
          <div
            role="tablist"
            aria-label="Topic types"
            className={styles.tabs}
            style={
              {
                '--tab-count': types.length,
                '--tab-index': selected,
              } as React.CSSProperties
            }
          >
            <span aria-hidden="true" className={styles.thumb} />
            {types.map((t, i) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={i === selected}
                aria-label={t.choiceLabel}
                data-testid={`topic-type-tab-${t.id}`}
                data-topic-type={t.id}
                className={`${styles.sticker} ${styles.tab}`}
                onClick={() => jumpTo(i)}
              >
                <span className={styles.tabSticker}>
                  <IonIcon icon={TOPIC_TYPE_STICKERS[t.id].icon} />
                </span>
              </button>
            ))}
          </div>
          <div
            ref={carouselRef}
            className={styles.carousel}
            onScroll={onCarouselScroll}
            onPointerDown={() => {
              jumpTargetRef.current = null;
            }}
          >
            {types.map((t, i) => {
              const sticker = TOPIC_TYPE_STICKERS[t.id];
              return (
                <button
                  key={t.id}
                  type="button"
                  data-testid={`topic-type-chip-${t.id}`}
                  data-topic-type={t.id}
                  data-selected={i === selected}
                  className={`${styles.sticker} ${styles.card}`}
                  onClick={() => onPick(t.id)}
                >
                  <span aria-hidden="true" className={styles.stickers}>
                    <span
                      className={`${styles.accentSticker} ${styles.accentTop}`}
                    >
                      <IonIcon icon={sticker.accentIcons[0]} />
                    </span>
                    <span className={styles.mainSticker}>
                      <IonIcon icon={sticker.icon} />
                    </span>
                    <span
                      className={`${styles.accentSticker} ${styles.accentBottom}`}
                    >
                      <IonIcon icon={sticker.accentIcons[1]} />
                    </span>
                  </span>
                  <span className={styles.label}>
                    <span className={styles.name}>{t.choiceLabel}</span>
                    <span className={styles.tagline}>{sticker.tagline}</span>
                  </span>
                  <span className={styles.examples}>
                    <span className={styles.examplesLead}>Think:</span>{' '}
                    {stickerExamples(t).join(' · ')}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </IonContent>
      {current && (
        <IonFooter className={`ion-no-border ${styles.footer}`}>
          <IonButton
            expand="block"
            className={styles.start}
            data-testid="topic-type-start"
            onClick={() => onPick(current.id)}
          >
            {startTopicLabel(current)}
          </IonButton>
        </IonFooter>
      )}
    </>
  );
};

export default TopicTypePickerSheet;
